/**
 * Generic Supabase Realtime → Zustand store synchronizer (back-office wide).
 *
 * Problem this solves
 * -------------------
 * Every admin list store fetched its data once on mount and then went stale:
 * a feature added, an article published, an image attached on one screen never
 * propagated to the lists already on screen, so the only fix was a manual page
 * refresh. The single exception was the conversations inbox, which carries its
 * own bespoke, high-frequency Realtime logic (per-row merge, optimistic echo).
 *
 * Rather than hand-roll fragile per-row merges everywhere (Realtime payloads
 * never carry the joined relations a list query projects — categories, tags,
 * modules… — nor the list's sort order), this helper takes the robust, boring
 * route: on ANY change to a watched table, it triggers a *debounced, silent
 * re-fetch* of the owning store's collection. Human-paced admin edits make the
 * extra round-trip a non-issue, and the result is always exactly consistent
 * with what a full reload would show — which is the whole point.
 *
 * Robustness (mirrors conversationsStore, the proven reference)
 * -------------------------------------------------------------
 * - `setAuth()` before joining: a stale/rotated JWT makes `postgres_changes`
 *   silently fail RLS and drop every event.
 * - Reconnect with capped exponential backoff on CHANNEL_ERROR/TIMED_OUT/CLOSED,
 *   only while the page is awake: a hidden tab or an offline browser neither
 *   retries nor counts, it re-joins at the wake (`visibilitychange`, `online`),
 *   which also starts the count over.
 * - Catch-up `onChange()` on every (re)connect AND on tab focus / network
 *   recovery — `postgres_changes` never replays rows missed while the socket
 *   was down (tab asleep, network blip), so without this the list silently
 *   drifts after a wake-up.
 * - One health watch for all channels: a socket that cannot come back warns
 *   once per outage, naming the channels down and why (`err.message` of the
 *   subscribe callback tells a network cut from a refused private join).
 * - A module-level registry lets `signOut` tear every subscription down at once
 *   (no leaked channels carrying a logged-out user's token).
 */
import { REALTIME_CHANNEL_STATES } from '@supabase/supabase-js';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '../supabase';
import { logInfo, logWarn } from '../logger';
import { isPageAwake } from '../net/connectivity';
import { isTransientNetworkError } from '../net/transientError';
import { stopAllPeriodicReads } from '../net/periodicRead';

/**
 * A single transient socket drop closes every channel at once, then backoff
 * re-joins them — that self-healing cycle is normal and must NOT page. Only a
 * channel that fails to reconnect this many times in a row, while the page is
 * awake (visible, online), is genuinely stuck and worth a warning.
 */
const RECONNECT_WARN_THRESHOLD = 4;

/** All live syncs, so logout can tear them all down in one call. */
const activeSyncs = new Set<RealtimeSync>();

/**
 * One watch over the socket's health for every channel. A socket that cannot
 * come back drops all channels together: that is ONE outage, worth ONE warning
 * naming the channels down — not one per channel and per status (CHANNEL_ERROR
 * then CLOSED arrive for the same failure). The episode ends when every channel
 * is back, or at the page's wake (tab seen again, network back), which starts
 * the count over.
 */
const downChannels = new Map<string, string | undefined>();
let outageWarned = false;

function channelUp(name: string): void {
  downChannels.delete(name);
  if (downChannels.size === 0) outageWarned = false;
}

function channelDown(name: string, status: string, attempts: number, reason: string | undefined): void {
  downChannels.set(name, reason);
  const context = { status, attempts, reason };
  if (attempts >= RECONNECT_WARN_THRESHOLD && !outageWarned && isPageAwake()) {
    outageWarned = true;
    logWarn('realtime', 'realtime channels cannot reconnect', {
      ...context,
      channel: name,
      down: [...downChannels.entries()].map(([channel, why]) => ({ channel, reason: why })),
    });
    return;
  }
  logInfo('realtime', `${name} ${status}`, context);
}

function newOutageEpisode(): void {
  outageWarned = false;
}

export interface RealtimeSyncOptions {
  /**
   * Unique channel name (one channel per store, e.g. `rt-knowledge`). With `broadcast`, it is
   * the topic the server sends on (`realtime.send(…, topic, private := true)`).
   */
  channelName: string;
  /** `public` tables whose changes should trigger a re-fetch of this store. */
  tables?: readonly string[];
  /**
   * A private Broadcast event sent by the server itself (triggers calling `realtime.send`),
   * instead of — or with — table changes: the server decides what is worth a re-fetch, the
   * tables stay out of the publication, and only users allowed by the RLS of
   * `realtime.messages` on this topic receive it.
   */
  broadcast?: { event: string };
  /**
   * Extra handlers bound on the channel before it joins, for a store that merges rows itself
   * (the inbox): the reconnect, the catch-up and the health watch stay this helper's.
   */
  bind?: (channel: RealtimeChannel) => RealtimeChannel;
  /** Told when the channel goes live (`true`) or drops (`false`) — e.g. to poll meanwhile. */
  onStatus?: (live: boolean) => void;
  /**
   * Silent re-fetch of the store's collection(s). MUST NOT toggle a visible
   * loading flag (no skeleton flash) and SHOULD swallow its own errors — a
   * failed catch-up is logged, not surfaced. Called on connect, on every
   * watched change (debounced), and on tab/network wake-up.
   */
  onChange: () => Promise<void> | void;
  /** Debounce window to coalesce change bursts (default 300 ms). */
  debounceMs?: number;
}

export interface RealtimeSync {
  /** Idempotent: joins the channel if not already live. Resolves once the channel is bound. */
  subscribe: () => Promise<void>;
  /** Leaves the channel, cancels timers, detaches lifecycle listeners. */
  unsubscribe: () => void;
  /** True while a channel object exists (joined or reconnecting). */
  isActive: () => boolean;
}

function errMessage(err: unknown): string {
  return err instanceof Error ? err.message : String(err);
}

/**
 * Create — but do not yet join — a Realtime sync for one store. Call
 * `subscribe()` once the first fetch has populated the store; call
 * `unsubscribe()` on teardown (or rely on {@link teardownAllRealtimeSyncs}).
 */
export function createRealtimeSync(options: RealtimeSyncOptions): RealtimeSync {
  const { channelName, tables = [], broadcast, bind, onChange, onStatus } = options;
  const debounceMs = options.debounceMs ?? 300;

  let channel: RealtimeChannel | null = null;
  let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  let debounceTimer: ReturnType<typeof setTimeout> | null = null;
  let reconnectAttempts = 0;
  let teardown = false;
  let joining: Promise<void> | null = null;
  let detachLifecycle: (() => void) | null = null;

  const runChange = () => {
    try {
      void Promise.resolve(onChange()).catch((err) =>
        logWarn('realtime', `${channelName} onChange failed`, { message: errMessage(err) }),
      );
    } catch (err) {
      logWarn('realtime', `${channelName} onChange threw`, { message: errMessage(err) });
    }
  };

  const scheduleChange = () => {
    if (debounceTimer) clearTimeout(debounceTimer);
    debounceTimer = setTimeout(() => {
      debounceTimer = null;
      runChange();
    }, debounceMs);
  };

  const clearReconnect = () => {
    if (reconnectTimer) {
      clearTimeout(reconnectTimer);
      reconnectTimer = null;
    }
  };

  /**
   * Reconnect with capped exponential backoff — re-subscribing re-asserts auth.
   * A hidden tab or an offline browser neither retries nor counts: the wake
   * (`visibilitychange`, `online`) re-joins.
   */
  const scheduleReconnect = () => {
    if (reconnectTimer || teardown || !isPageAwake()) return;
    const delay = Math.min(1000 * 2 ** Math.max(0, reconnectAttempts - 1), 30_000);
    reconnectTimer = setTimeout(() => {
      reconnectTimer = null;
      if (teardown || !isPageAwake()) return;
      void join();
    }, delay);
  };

  /** Re-assert/catch up on tab focus or network recovery (timers stall when hidden). */
  const bindLifecycle = () => {
    if (detachLifecycle || typeof window === 'undefined') return;
    const onWake = () => {
      if (!isPageAwake()) return;
      // A new start: the failures counted before the sleep say nothing about now.
      reconnectAttempts = 0;
      newOutageEpisode();
      if (!channel || channel.state !== REALTIME_CHANNEL_STATES.joined) {
        clearReconnect();
        void join();
      } else runChange();
    };
    window.addEventListener('online', onWake);
    if (typeof document !== 'undefined') document.addEventListener('visibilitychange', onWake);
    detachLifecycle = () => {
      window.removeEventListener('online', onWake);
      if (typeof document !== 'undefined') document.removeEventListener('visibilitychange', onWake);
    };
  };

  const removeChannel = () => {
    const old = channel;
    // Forget it first: the CLOSED its removal fires belongs to a channel we no longer follow.
    channel = null;
    if (old) supabase.removeChannel(old);
  };

  const onChannelStatus = (ch: RealtimeChannel, status: string, err: Error | undefined) => {
    if (channel !== ch) return; // a channel we already replaced or removed
    if (status === 'SUBSCRIBED') {
      reconnectAttempts = 0;
      channelUp(channelName);
      onStatus?.(true);
      runChange(); // catch-up: close the gap left by events fired while down
      return;
    }
    if (teardown) return; // intentional unsubscribe — don't fight it
    if (status !== 'CHANNEL_ERROR' && status !== 'TIMED_OUT' && status !== 'CLOSED') return;
    onStatus?.(false);
    // Only a failure while the page is awake counts; CHANNEL_ERROR then CLOSED for the same
    // failure count once (the reconnect is already scheduled for the second).
    if (isPageAwake() && !reconnectTimer) reconnectAttempts += 1;
    // `err` tells a network cut from a refused join (a private channel's RLS).
    channelDown(channelName, status, reconnectAttempts, err?.message);
    scheduleReconnect();
  };

  const doJoin = async () => {
    removeChannel();

    // Carry the current staff JWT before joining: a stale/rotated token makes
    // postgres_changes silently fail RLS and drop every event.
    try {
      await supabase.realtime.setAuth();
    } catch (err) {
      // Waking up, the token refresh fails with the network: a cut, not a bug.
      const log = isTransientNetworkError(err) ? logInfo : logWarn;
      log('realtime', `${channelName} setAuth failed`, { message: errMessage(err) });
    }
    if (teardown) return;

    let ch = supabase.channel(channelName, broadcast ? { config: { private: true } } : undefined);
    for (const table of tables) {
      ch = ch.on('postgres_changes', { event: '*', schema: 'public', table }, () => {
        scheduleChange();
      });
    }
    if (broadcast) ch = ch.on('broadcast', { event: broadcast.event }, () => scheduleChange());
    if (bind) ch = bind(ch);
    channel = ch;
    const joined = ch;
    ch.subscribe((status, err) => onChannelStatus(joined, status, err));

    bindLifecycle();
    activeSyncs.add(sync);
  };

  const join = (): Promise<void> => {
    if (joining) return joining;
    if (channel && channel.state === REALTIME_CHANNEL_STATES.joined) return Promise.resolve();
    joining = doJoin().finally(() => {
      joining = null;
    });
    return joining;
  };

  const sync: RealtimeSync = {
    subscribe: () => {
      // Wanted again (also while a join is in flight: it then completes instead of bailing out).
      teardown = false;
      return join();
    },
    unsubscribe: () => {
      teardown = true;
      clearReconnect();
      if (debounceTimer) {
        clearTimeout(debounceTimer);
        debounceTimer = null;
      }
      detachLifecycle?.();
      detachLifecycle = null;
      removeChannel();
      channelUp(channelName);
      activeSyncs.delete(sync);
    },
    isActive: () => channel !== null,
  };

  return sync;
}

/**
 * Tear down every live Realtime sync and every periodic read — call on sign-out
 * (useAuth does, on `signOut()` and on an expired session's SIGNED_OUT) so no
 * channel keeps a logged-out user's token alive and no reader keeps polling.
 */
export function teardownAllRealtimeSyncs(): void {
  for (const sync of [...activeSyncs]) sync.unsubscribe();
  stopAllPeriodicReads();
}

/** Test seam: forget the shared outage state between tests. */
export function resetRealtimeHealthForTests(): void {
  downChannels.clear();
  outageWarned = false;
}
